/**
 * navigation.ts
 *
 * Navigation helpers for ACUL screen transitions.
 *
 * CRITICAL AUTH0 PLATFORM NOTES (read before editing)
 * ====================================================
 *
 * 1. SCREEN-TO-SCREEN ROUTING WITHIN UNIVERSAL LOGIN
 *    Auth0 Universal Login manages screen progression internally via the
 *    `login-id.login({ username })` call.  After that call, Auth0 picks
 *    the next screen (`login-password`, `login-passwordless-email-code`,
 *    etc.) based on tenant connection configuration — your code cannot
 *    force a specific next screen from inside Universal Login.
 *
 * 2. PASSWORDLESS CONNECTIONS REQUIRE A NEW /authorize REQUEST
 *    If the tenant has BOTH database and passwordless connections enabled,
 *    routing to a passwordless OTP screen from `login-id` requires starting
 *    a brand-new /authorize request with `connection=email` or `connection=sms`.
 *    This EXITS Universal Login briefly and re-enters it on the OTP screen.
 *
 *    Function: `buildPasswordlessAuthorizeUrl()`
 *
 *    TODO: Fill in your tenant domain, client ID, and redirect URI.
 *    These values are typically passed in via Auth0 UL context (transaction
 *    params).  Do NOT hard-code credentials here — read them from the
 *    Universal Login context.
 *
 * 3. BACK NAVIGATION
 *    Within a single Universal Login session, going "back" means the user
 *    re-enters data on the previous screen.  Auth0 does NOT support a
 *    `history.back()` model — you cannot pop the UL stack.
 *
 *    Instead, the screens maintain their own in-screen view state (e.g.
 *    showing the identifier field again).  The identifier is persisted in
 *    sessionStorage under the key VS_IDENTIFIER_KEY (see useIdentifierRouting).
 *
 * 4. SIGNUP MULTI-STEP
 *    Auth0 ACUL does not natively support a custom "verify phone" step
 *    between `signup-id` and `signup-password`.  The phone OTP step in the
 *    mockup signup.html (Step 2) maps to:
 *      - A post-signup Auth0 Action that triggers SMS MFA enrollment, OR
 *      - The `mfa-sms-enrollment` / `mfa-sms-challenge` ACUL screens
 *        if phone MFA is configured as required on first login.
 *    TODO: configure an Auth0 Post-User-Registration Action or MFA policy.
 *
 * Junior devs: this file centralises all cross-screen navigation concerns.
 * Add new helpers here rather than scattering window.location calls in screens.
 */

// ---------------------------------------------------------------------------
// Session-storage key for persisting the identifier across screens
// ---------------------------------------------------------------------------

export const VS_IDENTIFIER_KEY = "vs.auth.identifier";

// ---------------------------------------------------------------------------
// Identifier persistence
// ---------------------------------------------------------------------------

/** Save the identifier so subsequent screens can read it. */
export function saveIdentifier(identifier: string): void {
  try {
    sessionStorage.setItem(VS_IDENTIFIER_KEY, identifier);
  } catch {
    // Private browsing / storage full — graceful no-op
  }
}

/** Read the saved identifier. Returns "" if not set. */
export function loadIdentifier(): string {
  try {
    return sessionStorage.getItem(VS_IDENTIFIER_KEY) ?? "";
  } catch {
    return "";
  }
}

/** Clear the saved identifier (call on successful login or explicit back). */
export function clearIdentifier(): void {
  try {
    sessionStorage.removeItem(VS_IDENTIFIER_KEY);
  } catch {
    // no-op
  }
}

// ---------------------------------------------------------------------------
// Passwordless /authorize redirect
// ---------------------------------------------------------------------------

export interface PasswordlessAuthorizeParams {
  /**
   * Auth0 domain, e.g. "your-tenant.us.auth0.com"
   * TODO: read from Universal Login context — transaction.params.auth0Domain
   * or inject via window.__AUTH0_UL_CONTEXT__ at build time.
   */
  domain: string;
  /** OAuth client_id from the UL context. */
  clientId: string;
  /** Where Auth0 redirects after authentication. */
  redirectUri: string;
  /** "email" for email OTP, "sms" for SMS OTP. */
  connection: "email" | "sms";
  /** The identifier pre-fills the login_hint so the UL OTP screen shows it. */
  loginHint?: string;
  /** PKCE code_challenge — REQUIRED for SPAs. */
  codeChallenge?: string;
  /** Always "S256" when using PKCE. */
  codeChallengeMethod?: "S256";
  /** state param for CSRF protection. */
  state?: string;
}

/**
 * Builds an /authorize URL that starts a passwordless flow.
 *
 * IMPORTANT: This triggers a full browser navigation — the user leaves the
 * current Universal Login page and Auth0 re-enters UL on the OTP screen.
 *
 * TODO: Obtain domain, clientId, redirectUri from the UL transaction context.
 * Do NOT hard-code tenant credentials.
 */
export function buildPasswordlessAuthorizeUrl({
  domain,
  clientId,
  redirectUri,
  connection,
  loginHint,
  codeChallenge,
  codeChallengeMethod,
  state,
}: PasswordlessAuthorizeParams): string {
  const url = new URL(`https://${domain}/authorize`);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("redirect_uri", redirectUri);
  url.searchParams.set("connection", connection);
  url.searchParams.set("scope", "openid profile email");
  if (loginHint) url.searchParams.set("login_hint", loginHint);
  if (codeChallenge) url.searchParams.set("code_challenge", codeChallenge);
  if (codeChallengeMethod)
    url.searchParams.set("code_challenge_method", codeChallengeMethod);
  if (state) url.searchParams.set("state", state);
  return url.toString();
}

/**
 * Navigate to a passwordless /authorize flow.
 *
 * TODO: Replace the placeholder values with real values from the UL context.
 * Example wiring:
 *
 *   const { transaction } = useTransaction();
 *   redirectToPasswordless({
 *     domain:      transaction.domain,          // TODO: check SDK property name
 *     clientId:    transaction.clientId,        // TODO: check SDK property name
 *     redirectUri: window.location.origin,
 *     connection:  "email",
 *     loginHint:   identifier,
 *   });
 */
export function redirectToPasswordless(
  params: PasswordlessAuthorizeParams
): void {
  const url = buildPasswordlessAuthorizeUrl(params);
  window.location.href = url;
}
